import { ApiError, request } from './client'
import {
  asObject,
  bool,
  count,
  num,
  objects,
  optBool,
  optNum,
  optStr,
  optTime,
  raw,
  str,
  strings,
  text,
  time,
} from './parse'
import type { Money } from './threads'

/**
 * The inventory as the panel reads it: properties, the units inside them, and
 * the offers on each unit.
 *
 * Three types because they are three things (CONTEXT.md). A property is the
 * bricks; a unit is what a tenant actually gets, and a room is a unit in its own
 * right rather than a field on a flat; a listing is one commercial offer on a
 * unit, and a unit collects several over time.
 *
 * The wire contract this file parses:
 *
 *   GET  /api/properties?limit=&offset=      listings.read
 *   GET  /api/properties/{id}                listings.read
 *   GET  /api/listings/{id}/history?limit=   listings.read
 *   POST /api/listings/{id}/verify           listings.verify
 *
 * Amounts always arrive in the shape `{amount_minor, currency, kind}` with a
 * null figure exactly when the kind is unknown, which is what stops an amount
 * nobody has stated from reaching a screen as a zero (invariant 2).
 */

/**
 * An amount, read the same way everywhere in this API.
 *
 * Written out again rather than imported from the thread module's private
 * parser: the check that `amount_minor` is a number is the whole guard, and a
 * second copy of four lines is cheaper than exporting a parser that would then
 * be tempting to loosen.
 */
function parseMoney(value: unknown, currency: string): Money {
  if (value === undefined || value === null) {
    // No amount at all is an amount nobody has stated. It is never zero.
    return { amountMinor: null, currency, kind: 'unknown' }
  }
  const source = asObject(value, 'an amount')
  const minor = source['amount_minor']
  return {
    amountMinor: typeof minor === 'number' ? minor : null,
    currency: optStr(source, 'currency') ?? currency,
    kind: str(source, 'kind'),
  }
}

export interface PropertySummary {
  readonly id: string
  readonly city: string
  readonly addressNormalized: string
  /** Withheld by some publishers until a viewing is agreed. Empty is a fact about the source. */
  readonly addressExact: string
  readonly area: string
  readonly municipality: string
  readonly floor: number | undefined
  readonly elevator: boolean | undefined
  /**
   * How much is on this property.
   *
   * Optional, like `pending_drafts` on a conversation: the list query can answer
   * without them, and a screen that counted by opening every property would turn
   * one request into fifty. Absent means not counted, which the row says rather
   * than showing a zero.
   */
  readonly unitCount: number | undefined
  readonly listingCount: number | undefined
  /** Listings that may be shown to a client right now: verified and still checked. */
  readonly offerableCount: number | undefined
  readonly updatedAt: string | undefined
  /**
   * A photograph of the place, read from /api/media/{id}. Undefined when nobody
   * has uploaded one, which is most of them at first.
   */
  readonly coverMediaId: string | undefined
}

export interface PropertyPage {
  readonly properties: readonly PropertySummary[]
  readonly total: number
  /** Whether another page exists. Nothing counts the whole table, so this is the
   * honest half of "how many are there". */
  readonly more: boolean
  readonly limit: number
  readonly offset: number
}

export interface Property {
  readonly id: string
  readonly city: string
  readonly addressNormalized: string
  readonly addressExact: string
  readonly area: string
  readonly municipality: string
  readonly floor: number | undefined
  readonly elevator: boolean | undefined
  readonly heating: string
  readonly notes: string
  readonly createdAt: string | undefined
  readonly updatedAt: string | undefined
}

/**
 * Somebody attached to a property in one role.
 *
 * `confirmedAt` is undefined until a person checked the claim. Specification
 * §16.3 has agents presenting themselves as owners, so the claim and the
 * confirmation are two different facts and the screen keeps them apart.
 */
export interface PropertyContactLink {
  readonly id: string
  readonly contactId: string
  readonly role: string
  readonly displayName: string
  readonly confirmedAt: string | undefined
  readonly note: string
}

export interface PriceComponent {
  readonly id: string
  readonly component: string
  readonly period: string
  readonly amount: Money
  /** "Two months' deposit" as it was said. The figure follows the rent; the statement does not. */
  readonly monthsHundredths: number | undefined
  /**
   * How many people this price is for. Undefined means it does not depend on
   * that, which is every whole flat and every single place.
   */
  readonly forOccupants: number | undefined
  readonly note: string
}

export interface AvailabilityWindow {
  readonly id: string
  readonly from: string
  /** Undefined is open-ended, which is the usual answer for a long let. Not unknown. */
  readonly to: string | undefined
  readonly minMonthsHundredths: number | undefined
  readonly maxMonthsHundredths: number | undefined
  readonly renewable: boolean | undefined
  readonly moveInFlexibilityDays: number
  readonly note: string
}

export interface ListingSource {
  readonly id: string
  readonly sourceType: string
  readonly sourceName: string
  readonly url: string
  readonly sourceListingId: string | undefined
  /** What the source says we may do to reach the publisher. Recorded, never assumed. */
  readonly permittedContactMethod: string
  readonly lastSeenAt: string | undefined
  /** Set when the source stopped showing it: the observation that retires drafts quoting it. */
  readonly disappearedAt: string | undefined
}

export interface Verification {
  readonly id: string
  readonly verifiedAt: string
  readonly expiresAt: string
  readonly method: string
  /** Which facts this check covered. A call about the price says nothing about the dates. */
  readonly factsConfirmed: readonly string[]
  readonly evidence: string
  readonly note: string
}

export interface Listing {
  readonly id: string
  readonly unitId: string
  readonly title: string
  readonly status: string
  readonly statusReason: string
  readonly sourceType: string
  readonly contractTypeDeclared: string | undefined
  readonly contractRegistered: boolean | undefined
  readonly residenzaPossible: boolean | undefined
  /** The currency every amount on this listing is quoted in. */
  readonly currency: string
  readonly verifiedAt: string | undefined
  readonly verificationExpiresAt: string | undefined
  /** Something moved since the check. Not an expiry on its own, but a warning beside a fresh one. */
  readonly changedSinceCheck: boolean
  /**
   * Whether this may be put in front of a client right now — the server's
   * verdict, not ours (docs/adr/0008).
   *
   * Read rather than derived, and absent counts as false: a build that cannot
   * read the answer must not offer a flat on the strength of a timestamp it
   * interpreted itself (invariant 8).
   */
  readonly offerable: boolean
  /**
   * What this object still lacks before anybody can answer about it properly:
   * what the advert did not say, or what a person noticed later. Empty is the
   * ordinary state.
   */
  readonly infoNeeded: string
  readonly infoNeededAt: string | undefined
  readonly notes: string
  readonly priceComponents: readonly PriceComponent[]
  readonly availabilityWindows: readonly AvailabilityWindow[]
  readonly sources: readonly ListingSource[]
  readonly media: readonly MediaAsset[]
  readonly latestVerification: Verification | undefined
  readonly updatedAt: string | undefined
}

export interface Unit {
  readonly id: string
  readonly propertyId: string
  /**
   * The space this one is inside: a room's flat, a place's room. Undefined for a
   * flat, and for a room in a property whose flat is not on offer as a whole.
   *
   * The panel draws the tree from it, and it is what makes the offers answer for
   * each other: a let room stops its flat being offered (docs/adr/0010).
   */
  readonly parentUnitId: string | undefined
  readonly kind: string
  readonly label: string
  readonly bedrooms: number | undefined
  readonly beds: number | undefined
  readonly bathrooms: number | undefined
  /** As the owner states it. Undefined is unknown, and the matcher may not compare against it. */
  readonly capacity: number | undefined
  /** Floor area in square metres. Undefined is unmeasured, never zero. */
  readonly areaSqm: number | undefined
  readonly roomPrivate: boolean | undefined
  readonly furnished: boolean | undefined
  readonly notes: string
  readonly listings: readonly Listing[]
  /**
   * The spaces above and below this one that somebody has, and therefore why
   * this one cannot be offered however its own switch is set.
   *
   * Empty is the ordinary case. Without it the screen was lying: a flat with a
   * let room showed a green button and reached nobody.
   */
  readonly blockedBy: readonly BlockingSpace[]
}

/** One space somebody has, named the way a person would say it. */
export interface BlockingSpace {
  readonly kind: string
  readonly label: string
  readonly status: string
}

/** One stored photograph or video. The bytes are read from /api/media/{id}. */
export interface MediaAsset {
  readonly id: string
  readonly kind: string
  readonly mime: string
  readonly caption: string
}

export interface PropertyCard {
  readonly property: Property
  readonly contacts: readonly PropertyContactLink[]
  readonly units: readonly Unit[]
}

export interface ListingChange {
  readonly id: number
  readonly changedAt: string
  readonly field: string
  readonly oldValue: unknown
  readonly newValue: unknown
  readonly source: string
  readonly note: string
}

export interface ListingHistory {
  readonly changes: readonly ListingChange[]
  readonly verifications: readonly Verification[]
}

function parseSummary(source: Record<string, unknown>): PropertySummary {
  return {
    id: str(source, 'id'),
    city: text(source, 'city'),
    addressNormalized: text(source, 'address_normalized'),
    addressExact: text(source, 'address_exact'),
    area: text(source, 'area'),
    municipality: text(source, 'municipality'),
    floor: optNum(source, 'floor'),
    elevator: optBool(source, 'elevator'),
    unitCount: optNum(source, 'unit_count'),
    listingCount: optNum(source, 'listing_count'),
    offerableCount: optNum(source, 'offerable_count'),
    coverMediaId: optStr(source, 'cover_media_id'),
    updatedAt: optTime(source, 'updated_at'),
  }
}

export interface PropertyFilter {
  readonly limit?: number
  readonly offset?: number
  /** Matches the address or the area, anywhere in it. */
  readonly query?: string
  /** Keeps only addresses where some offer is in that state. */
  readonly status?: string
}

export function fetchProperties(filter: PropertyFilter, signal?: AbortSignal): Promise<PropertyPage> {
  const query = new URLSearchParams()
  if (filter.limit !== undefined) query.set('limit', String(filter.limit))
  if (filter.offset !== undefined && filter.offset > 0) query.set('offset', String(filter.offset))
  // Sent to the server rather than applied here: the list is paged, and
  // narrowing a page after it arrives shows four of fifty and calls it the
  // answer.
  if (filter.query !== undefined && filter.query !== '') query.set('q', filter.query)
  if (filter.status !== undefined && filter.status !== '') query.set('status', filter.status)

  return request(
    `/api/properties?${query.toString()}`,
    (body) => {
      const source = asObject(body, 'the property list')
      return {
        properties: objects(source, 'properties').map(parseSummary),
        total: count(source, 'total'),
    more: source.more === true,
        limit: count(source, 'limit'),
        offset: count(source, 'offset'),
      }
    },
    signal === undefined ? {} : { signal },
  )
}

export function parseVerification(value: unknown): Verification {
  const source = asObject(value, 'a check')
  return {
    id: str(source, 'id'),
    verifiedAt: time(source, 'verified_at'),
    expiresAt: time(source, 'expires_at'),
    method: text(source, 'method'),
    factsConfirmed: strings(source, 'facts_confirmed'),
    evidence: text(source, 'evidence'),
    note: text(source, 'note'),
  }
}

export function parseListing(value: unknown): Listing {
  const source = asObject(value, 'a listing')
  const currency = optStr(source, 'currency') ?? 'EUR'
  const verification = source['latest_verification']

  return {
    id: str(source, 'id'),
    unitId: text(source, 'unit_id'),
    title: text(source, 'title'),
    status: str(source, 'status'),
    statusReason: text(source, 'status_reason'),
    sourceType: text(source, 'source_type'),
    contractTypeDeclared: optStr(source, 'contract_type_declared'),
    contractRegistered: optBool(source, 'contract_registered'),
    residenzaPossible: optBool(source, 'residenza_possible'),
    currency,
    verifiedAt: optTime(source, 'verified_at'),
    verificationExpiresAt: optTime(source, 'verification_expires_at'),
    changedSinceCheck: bool(source, 'changed_since_check'),
    offerable: bool(source, 'offerable'),
    infoNeeded: text(source, 'info_needed'),
    infoNeededAt: optTime(source, 'info_needed_at'),
    notes: text(source, 'notes'),
    priceComponents: objects(source, 'price_components').map((component) => ({
      id: str(component, 'id'),
      component: str(component, 'component'),
      period: text(component, 'period'),
      amount: parseMoney(component['amount'], currency),
      monthsHundredths: optNum(component, 'months_hundredths'),
      forOccupants: optNum(component, 'for_occupants'),
      note: text(component, 'note'),
    })),
    availabilityWindows: objects(source, 'availability_windows').map((window) => ({
      id: str(window, 'id'),
      from: time(window, 'from'),
      to: optTime(window, 'to'),
      minMonthsHundredths: optNum(window, 'min_months_hundredths'),
      maxMonthsHundredths: optNum(window, 'max_months_hundredths'),
      renewable: optBool(window, 'renewable'),
      moveInFlexibilityDays: count(window, 'move_in_flexibility_days'),
      note: text(window, 'note'),
    })),
    sources: objects(source, 'sources').map((entry) => ({
      id: str(entry, 'id'),
      sourceType: text(entry, 'source_type'),
      sourceName: text(entry, 'source_name'),
      url: text(entry, 'url'),
      sourceListingId: optStr(entry, 'source_listing_id'),
      permittedContactMethod: text(entry, 'permitted_contact_method'),
      lastSeenAt: optTime(entry, 'last_seen_at'),
      disappearedAt: optTime(entry, 'disappeared_at'),
    })),
    media: objects(source, 'media').map((asset) => ({
      id: str(asset, 'id'),
      kind: text(asset, 'kind'),
      mime: text(asset, 'mime'),
      caption: text(asset, 'caption'),
    })),
    latestVerification:
      verification === undefined || verification === null ? undefined : parseVerification(verification),
    updatedAt: optTime(source, 'updated_at'),
  }
}

export function fetchProperty(propertyId: string, signal?: AbortSignal): Promise<PropertyCard> {
  return request(
    `/api/properties/${encodeURIComponent(propertyId)}`,
    (body) => {
      const source = asObject(body, 'the property')
      const property = asObject(source['property'], '"property"')

      return {
        property: {
          id: str(property, 'id'),
          city: text(property, 'city'),
          addressNormalized: text(property, 'address_normalized'),
          addressExact: text(property, 'address_exact'),
          area: text(property, 'area'),
          municipality: text(property, 'municipality'),
          floor: optNum(property, 'floor'),
          elevator: optBool(property, 'elevator'),
          heating: text(property, 'heating'),
          notes: text(property, 'notes'),
          createdAt: optTime(property, 'created_at'),
          updatedAt: optTime(property, 'updated_at'),
        },
        contacts: objects(source, 'contacts').map((contact) => ({
          id: str(contact, 'id'),
          contactId: text(contact, 'contact_id'),
          role: text(contact, 'role'),
          displayName: text(contact, 'display_name'),
          confirmedAt: optTime(contact, 'confirmed_at'),
          note: text(contact, 'note'),
        })),
        units: objects(source, 'units').map((unit) => ({
          id: str(unit, 'id'),
          propertyId: text(unit, 'property_id'),
          parentUnitId: optStr(unit, 'parent_unit_id'),
          kind: text(unit, 'kind'),
          label: text(unit, 'label'),
          bedrooms: optNum(unit, 'bedrooms'),
          beds: optNum(unit, 'beds'),
          bathrooms: optNum(unit, 'bathrooms'),
          capacity: optNum(unit, 'capacity'),
          areaSqm: optNum(unit, 'area_sqm'),
          roomPrivate: optBool(unit, 'room_private'),
          furnished: optBool(unit, 'furnished'),
          notes: text(unit, 'notes'),
          listings: objects(unit, 'listings').map(parseListing),
          blockedBy: objects(unit, 'blocked_by').map((blocker) => ({
            kind: text(blocker, 'kind'),
            label: text(blocker, 'label'),
            status: text(blocker, 'status'),
          })),
        })),
      }
    },
    signal === undefined ? {} : { signal },
  )
}

export function fetchListingHistory(
  listingId: string,
  limit: number,
  signal?: AbortSignal,
): Promise<ListingHistory> {
  return request(
    `/api/listings/${encodeURIComponent(listingId)}/history?limit=${String(limit)}`,
    (body) => {
      const source = asObject(body, 'the history')
      return {
        changes: objects(source, 'changes').map((change) => ({
          id: num(change, 'id'),
          changedAt: time(change, 'changed_at'),
          field: str(change, 'field'),
          oldValue: raw(change, 'old_value'),
          newValue: raw(change, 'new_value'),
          source: text(change, 'source'),
          note: text(change, 'note'),
        })),
        verifications: objects(source, 'verifications').map(parseVerification),
      }
    },
    signal === undefined ? {} : { signal },
  )
}

/**
 * How a check was made. The set is closed in `domain/listing`, and a page that
 * offered a fifth would only produce refusals.
 */
export const VERIFICATION_METHODS = ['owner_message', 'call', 'source_page', 'visit'] as const
export type VerificationMethod = (typeof VERIFICATION_METHODS)[number]

/**
 * What a check can cover.
 *
 * Separate because they go stale at different speeds: confirming the rent says
 * nothing about whether the room is still free, and the matcher is entitled to
 * know which half of the offer was actually checked.
 */
export const VERIFICATION_FACTS = ['availability', 'price', 'conditions', 'source'] as const
export type VerificationFact = (typeof VERIFICATION_FACTS)[number]

export interface VerifyInput {
  readonly listingId: string
  readonly method: VerificationMethod
  /**
   * Where the proof lives: a message, a URL, a call note somebody can reopen.
   *
   * Required by the domain and required here. A check with nothing behind it is
   * somebody's recollection, and ADR 0008 makes the stored check the only answer
   * the assistant gives a client asking whether a room is still free.
   */
  readonly evidence: string
  readonly facts: readonly VerificationFact[]
  readonly note: string
}

export interface VerifyResult {
  readonly listing: Listing
  readonly verification: Verification
}

/**
 * The server's code for a check that showed nothing.
 *
 * Checked as a code rather than by matching the sentence: the panel refuses an
 * empty evidence field itself, so seeing this back means the two disagree about
 * what counts, and that has to read as a specific refusal rather than as "could
 * not save".
 */
export const CODE_EVIDENCE_REQUIRED = 'evidence_required'

/** Whether a refusal was the server insisting on evidence. */
export function isEvidenceRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (typeof error.body !== 'object' || error.body === null) return false
  return (error.body as Record<string, unknown>)['code'] === CODE_EVIDENCE_REQUIRED
}

/**
 * Record that somebody checked this offer.
 *
 * No lifetime is sent. How long a check stays good is a setting (docs/adr/0008),
 * not a choice the person making the check gets to widen, and the answer carries
 * the expiry the server applied.
 */
export function verifyListing(input: VerifyInput): Promise<VerifyResult> {
  const note = input.note.trim()

  return request(
    `/api/listings/${encodeURIComponent(input.listingId)}/verify`,
    (body) => {
      const source = asObject(body, 'the check')
      return {
        listing: parseListing(source['listing']),
        verification: parseVerification(source['verification']),
      }
    },
    {
      method: 'POST',
      body: {
        method: input.method,
        evidence: input.evidence.trim(),
        facts_confirmed: input.facts,
        ...(note === '' ? {} : { note }),
      },
    },
  )
}
